import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuthQuery as useQuery } from '../hooks/useAuthQuery';
import { useTranslation } from 'react-i18next';
import { Button } from '../../catalyst-components/button';
import { Field, Label } from '../../catalyst-components/fieldset';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { BaseContext } from '../base-context';
import { Id } from '../../convex/_generated/dataModel';
import { Banner } from '../../catalyst-components/banner';
import { useStableQuery } from '../hooks/useStableQuery';
interface ResumeUploadProps {
  handleResumeUploaded: (resumeUploaded: boolean) => void;
}

export const ResumeUploadComponent = ({ handleResumeUploaded }: ResumeUploadProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [alert, setAlert] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const generateUploadUrl = useMutation(api.user_metadata.generateUploadUrl);
  const saveFile = useMutation(api.user_metadata.saveFile);
  const { convexUserId } = useContext(BaseContext);
  const userMetadata = useStableQuery(api.user_metadata.getUserMetadata, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");

  useEffect(() => {
    if (userMetadata?.resume_summary) {
      setIsUploaded(true);
      setIsUploading(false);
      setIsProcessing(false);
      handleResumeUploaded(true);
    }
  }, [userMetadata?.resume_summary]);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    setAlert(null);
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    // Check file type
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!validTypes.includes(file.type) && !file.type.startsWith('image/')) {
      setAlert(t('resume.errorInvalidFormat'));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    if (file.size > 5 * 1024 * 1024) {
      setAlert(t('resume.errorFileTooLarge'));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setFile(file);
  }, []);

  const uploadFile = useCallback(async () => {
    if (!file) return;

    try {
      setIsUploading(true);
    
      const uploadUrl = await generateUploadUrl();

      const result = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type || "" },
        body: file,
      });

      if (result.ok) {
        const { storageId } = await result.json();
        await saveFile({ user_id: convexUserId as Id<"users">, storageId, mimeType: file?.type || "application/pdf" });
        setIsUploading(false);
        setIsProcessing(true);
      }
    } catch (error) {
      setIsUploading(false);
      setIsProcessing(false);
      setIsUploaded(false);
      setFile(null);
      setAlert(t('resume.uploadError'));
    }
  }, [file, generateUploadUrl, saveFile, convexUserId]);

  return (
    <div className="mt-4">
    <Field>
      {alert && <Banner color="error">{alert}</Banner>}
      {!isUploaded && !isProcessing && !isUploading && <Label>{t('resume.label')}</Label>}
      <div className="flex items-center space-x-2">
        {isUploaded ? (
          <div className="flex items-center text-primary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="ml-2 text-lg font-medium text-foreground">{t('resume.completedText')}</span>
          </div>
        ) : isProcessing ? (
          <div className="flex items-center text-primary">
            <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-8 w-8" />
            <span className="ml-3 text-base font-medium text-foreground">{t('resume.processingMessage')}</span>
          </div>
        ) : isUploading ? (
          <div className="flex items-center text-primary">
            <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-8 w-8" />
            <span className="ml-3 text-base font-medium text-foreground">{t('resume.uploadingMessage')}</span>
          </div>
        ) : (
          <div className="flex flex-col space-y-3">
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,image/*"
                onChange={handleFileChange}
                className="hidden"
                id="resume-upload"
              />
              <Button
                color="secondary"
                className="rounded-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {t('resume.chooseFileButton')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {file?.name || t('resume.noFileChosen')}
              </span>
            </div>
            {file && (
              <Button
                color="primary"
                className="rounded-full"
                onClick={() => uploadFile()}
                disabled={isUploading}
              >
                {t('resume.uploadButton')}
              </Button>
            )}
          </div>
        )}
      </div>
    </Field>
  </div>
  );
};

export const ResumeUpload = React.memo(ResumeUploadComponent); 